import { useJobCardDetail } from "@features/wx/dispatcher/hooks";
import { wxUtils } from "@features/wx/utils";
import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import {
  Activity,
  ArrowLeft,
  CheckSquare,
  ExternalLink,
  FileText,
  HelpCircle,
  Package,
  Wrench,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AssetServiceOrderKPI } from "./dashboard/assetDashboard";
import { useCurrency } from "@shared/selectors/globalSelectors";

// Service type configuration
const serviceTypeConfig = {
  Asset: {
    title: "Asset Details",
    icon: "🏗️",
    fields: {
      id: "Asset ID",
      name: "Asset Name",
      location: "Location",
      condition: "Condition",
      riskScore: "Risk Score",
      status: "Status",
    },
  },
  Facility: {
    title: "Facility Details",
    icon: "🏢",
    fields: {
      id: "Facility ID",
      name: "Facility Name",
      location: "Location",
      condition: "Condition",
      riskScore: "Risk Score",
      status: "Status",
    },
  },
  System: {
    title: "System Details",
    icon: "⚙️",
    fields: {
      id: "System ID",
      name: "System Name",
      location: "Location",
      condition: "Condition",
      riskScore: "Risk Score",
      status: "Status",
    },
  },
  Network: {
    title: "Network Details",
    icon: "🌐",
    fields: {
      id: "Network ID",
      name: "Network Name",
      location: "Location",
      condition: "Condition",
      riskScore: "Risk Score",
      status: "Status",
    },
  },
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => (
  <div>
    <label className="text-sm font-medium text-gray-500">{label}</label>
    <p className="text-base text-gray-900">{value || "N/A"}</p>
  </div>
);

const badgeColors: Record<string, string> = {
  critical: "bg-red-100 text-red-800 border-red-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  medium: "bg-blue-100 text-blue-800 border-blue-200",
  low: "bg-green-100 text-green-800 border-green-200",
  document: "bg-blue-100 text-blue-800",
  instruction: "bg-blue-100 text-blue-800",
  "video link": "bg-red-100 text-red-800",
  checklist: "bg-green-100 text-green-800",
  hotline: "bg-purple-100 text-purple-800",
};

const getBadgeClass = (key?: string) =>
  badgeColors[key?.toLowerCase()] || "bg-gray-100 text-gray-800";

const renderDocumentLink = (doc: any) => {
  if (doc.file)
    return (
      <a
        href={doc.file}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center text-blue-600 hover:text-blue-800"
      >
        <FileText className="h-4 w-4 mr-1" /> View File{" "}
        <ExternalLink className="h-3 w-3 ml-1" />
      </a>
    );
  if (doc.link)
    return (
      <a
        href={doc.link}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center text-blue-600 hover:text-blue-800"
      >
        <ExternalLink className="h-4 w-4 mr-1" /> Open Link{" "}
        <ExternalLink className="h-3 w-3 ml-1" />
      </a>
    );
  return <span className="text-muted-foreground">No link/file</span>;
};

const createPaginationConfig = (
  data: any[],
  page: number,
  perPage: number
) => ({
  currentPage: page,
  totalPages: Math.ceil(data.length / perPage),
  totalItems: data.length,
  itemsPerPage: perPage,
  startItem: (page - 1) * perPage + 1,
  endItem: Math.min(page * perPage, data.length),
});

const getPaginatedData = (data: any[], page: number, perPage: number) =>
  data.slice((page - 1) * perPage, page * perPage);

const renderTableTab = (
  label: string,
  icon: React.ReactNode,
  data: any[] | undefined,
  columns: any[],
  rowKey: string,
  page: number,
  perPage: number,
  setPage: (n: number) => void
) => ({
  label,
  icon,
  count: data?.length || 0,
  component: (
    <GenericTable
      data={getPaginatedData(data || [], page, perPage)}
      columns={columns}
      rowKey={rowKey}
      actionsType="dropdown"
      pagination={createPaginationConfig(data || [], page, perPage)}
      onPageChange={setPage}
      emptyMessage={`No ${label.toLowerCase()} found`}
    />
  ),
});

const tasksColumns = [
  {
    key: "taskName",
    header: "Task Name",
    render: (v: string) => (
      <span className="font-medium text-gray-900">{v}</span>
    ),
  },
  {
    key: "tatMins",
    header: "TAT (Mins)",
    render: (v: number) => <span>{v}</span>,
  },
  {
    key: "downtimeMins",
    header: "Downtime (Mins)",
    render: (v: number) => (
      <span className={v > 0 ? "text-orange-600" : "text-gray-700"}>{v}</span>
    ),
  },
  {
    key: "photoRequired",
    header: "Photo Required",
    type: "badge" as const,
    badgeConfig: {
      getClassName: (v: string) =>
        v === "Yes"
          ? "bg-green-100 text-green-800"
          : "bg-gray-100 text-gray-800",
    },
  },
];

const materialsColumns = [
  {
    key: "name",
    header: "Name",
    render: (v: string) => (
      <span className="font-medium text-gray-900">{v}</span>
    ),
  },
  { key: "size", header: "Size", render: (v: string) => <span>{v}</span> },
  {
    key: "quantity",
    header: "Quantity",
    render: (v: number) => <span>{v}</span>,
  },
  {
    key: "cost",
    header: "Cost",
    render: (v: number) => (
      <span className="font-semibold text-green-600">{useCurrency()} {v?.toFixed(2)}</span>
    ),
  },
];

const readingsColumns = [
  {
    key: "name",
    header: "Name",
    render: (v: string) => (
      <span className="font-medium text-gray-900">{v}</span>
    ),
  },
  { key: "unit", header: "Unit", render: (v: string) => <span>{v}</span> },
  { key: "range", header: "Range", render: (v: string) => <span>{v}</span> },
  {
    key: "photoRequired",
    header: "Photo Required",
    type: "badge" as const,
    badgeConfig: {
      getClassName: (v: string) =>
        v === "Yes"
          ? "bg-green-100 text-green-800"
          : "bg-gray-100 text-gray-800",
    },
  },
];

const helpResourcesColumns = [
  {
    key: "resourceName",
    header: "Resource Name",
    render: (v: string) => (
      <span className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer">
        {v}
      </span>
    ),
  },
  {
    key: "resourceType",
    header: "Resource Type",
    type: "badge" as const,
    badgeConfig: { getClassName: getBadgeClass },
  },
  {
    key: "details",
    header: "Details",
    render: (_: any, row: any) => renderDocumentLink(row),
  },
];

const AssetPendingServiceOrderView = () => {
  const navigate = useNavigate();
  const { id, serviceType } = useParams<{ id: string; serviceType: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Validate service type and fallback to Asset if invalid
  const validServiceType =
    serviceType && serviceType in serviceTypeConfig ? serviceType : "Asset";

  const currentServiceConfig = serviceTypeConfig[validServiceType];
  console.log("Current Service Config:", currentServiceConfig);

  const { data: jobCardResponse } = useJobCardDetail({ id });
  const transformedData = useMemo(
    () => jobCardResponse && wxUtils.transformJobCardDetail(jobCardResponse),
    [jobCardResponse]
  );

  const {
    serviceOrderData,
    assetDetails,
    jobOverview,
    tasksData,
    materialsData,
    readingsData,
    helpResourcesData,
  } = transformedData || {};

  const tabComponents = useMemo(
    () => ({
      overview: {
        label: "Overview",
        icon: <Activity className="h-4 w-4" />,
        component: (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DetailRow
              label="Description"
              value={serviceOrderData?.description}
            />
            <DetailRow
              label="Required Field Force"
              value={jobOverview?.requiredFieldforce}
            />
            <DetailRow label="Estimated Efforts" value={jobOverview?.efforts} />
            <DetailRow label="Downtime" value={jobOverview?.downtime} />
          </div>
        ),
      },
      tasks: renderTableTab(
        "Tasks",
        <CheckSquare className="h-4 w-4" />,
        tasksData,
        tasksColumns,
        "taskId",
        currentPage,
        itemsPerPage,
        setCurrentPage
      ),
      materials: renderTableTab(
        "Materials",
        <Package className="h-4 w-4" />,
        materialsData,
        materialsColumns,
        "materialId",
        currentPage,
        itemsPerPage,
        setCurrentPage
      ),
      readings: renderTableTab(
        "Readings",
        <Wrench className="h-4 w-4" />,
        readingsData,
        readingsColumns,
        "readingId",
        currentPage,
        itemsPerPage,
        setCurrentPage
      ),
      help: renderTableTab(
        "Help Resources",
        <HelpCircle className="h-4 w-4" />,
        helpResourcesData,
        helpResourcesColumns,
        "resourceId",
        currentPage,
        itemsPerPage,
        setCurrentPage
      ),
    }),
    [
      currentPage,
      serviceOrderData,
      jobOverview,
      tasksData,
      materialsData,
      readingsData,
      helpResourcesData,
    ]
  );

  if (!transformedData || !serviceOrderData)
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" />
    );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-xl flex gap-4 ml-5 items-center">
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700"
            onClick={() => navigate(-1)} // update route as needed
          />
          {/* Page Header */}
          <div className="flex flex-col">
            <PageHeader
              title={`Service Order - ${serviceOrderData.soName}`}
              subtitle={`${serviceOrderData.priority} priority ${validServiceType} service association management`}
            />
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="mx-auto px-6 py-8 space-y-8 bg-white">
        {/* KPI - Now using the separate component */}
        <AssetServiceOrderKPI serviceOrderData={serviceOrderData} />

        {/* Service Order + Dynamic Service Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-l-4">
            <CardHeader className="border-b p-2">
              <CardTitle>Service Order Details</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <DetailRow label="SO ID" value={serviceOrderData.jobCardNo} />
                <DetailRow label="Status" value={serviceOrderData.status} />
              </div>
              <DetailRow label="SO Name" value={serviceOrderData.soName} />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Priority
                  </label>
                  <Badge
                    className={`${getBadgeClass(
                      serviceOrderData.priority
                    )} px-2 py-1 text-xs font-medium border`}
                  >
                    {serviceOrderData.priority}
                  </Badge>
                </div>
                <DetailRow
                  label="Created On"
                  value={serviceOrderData.createdOn}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <DetailRow label="Area" value={serviceOrderData.area} />
                <DetailRow label="Sub Area" value={serviceOrderData.subarea} />
              </div>
              <DetailRow
                label="Service Association"
                value={serviceOrderData.utilityServices}
              />
            </CardContent>
          </Card>

          {/* Dynamic Service Details Card */}
          <Card className="border-l-4">
            <CardHeader className="border-b p-2">
              <CardTitle className="flex items-center gap-2">
                <span>{currentServiceConfig.icon}</span>
                {currentServiceConfig.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <DetailRow
                  label={currentServiceConfig.fields.id}
                  value={assetDetails?.assetId}
                />
                <DetailRow
                  label={currentServiceConfig.fields.name}
                  value={assetDetails?.assetName}
                />
              </div>
              <DetailRow
                label={currentServiceConfig.fields.location}
                value={assetDetails?.location}
              />
              <div className="grid grid-cols-2 gap-4">
                <DetailRow
                  label={currentServiceConfig.fields.condition}
                  value={assetDetails?.condition}
                />
                <DetailRow
                  label={currentServiceConfig.fields.riskScore}
                  value={assetDetails?.rpn}
                />
              </div>
              <DetailRow
                label={currentServiceConfig.fields.status}
                value={assetDetails?.status}
              />
            </CardContent>
          </Card>
        </div>

        {/* Job Requirements */}
        <Card>
          <CardHeader className="border-b p-2">
            <CardTitle>Job Requirements</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <Tabs
              defaultValue="overview"
              idPrefix="asset-job-req"
              tabComponents={tabComponents}
              tabsListClassName="grid grid-cols-5"
              disableUrlSync
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AssetPendingServiceOrderView;
